import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, ArrowRight, SkipForward, Sparkles } from 'lucide-react';

const QUESTIONS = [
  {
    q: 'When learning something new, I prefer to:',
    options: [
      { text: 'Watch a video or demonstration', style: 'visual' },
      { text: 'Listen to someone explain it', style: 'auditory' },
      { text: 'Read instructions or notes', style: 'reading' },
      { text: 'Try it hands-on straight away', style: 'kinesthetic' },
    ],
  },
  {
    q: 'In a training session, I remember most from:',
    options: [
      { text: 'Diagrams, images, and visual aids', style: 'visual' },
      { text: 'Group discussions and verbal instructions', style: 'auditory' },
      { text: 'Written handouts and note-taking', style: 'reading' },
      { text: 'Practical activities and role-plays', style: 'kinesthetic' },
    ],
  },
  {
    q: 'When solving a problem at work, I tend to:',
    options: [
      { text: 'Visualise the situation in my mind', style: 'visual' },
      { text: 'Talk it through with a colleague', style: 'auditory' },
      { text: 'Write down the options and analyse', style: 'reading' },
      { text: 'Jump in and try different approaches', style: 'kinesthetic' },
    ],
  },
  {
    q: 'My favourite type of online content is:',
    options: [
      { text: 'Videos and infographics', style: 'visual' },
      { text: 'Podcasts and audio content', style: 'auditory' },
      { text: 'Articles and written guides', style: 'reading' },
      { text: 'Interactive quizzes and simulations', style: 'kinesthetic' },
    ],
  },
  {
    q: 'If I needed to learn a new care procedure, I would:',
    options: [
      { text: 'Watch someone perform it first', style: 'visual' },
      { text: 'Have someone talk me through each step', style: 'auditory' },
      { text: 'Read the procedure manual carefully', style: 'reading' },
      { text: 'Practice it with supervision', style: 'kinesthetic' },
    ],
  },
];

const STYLE_INFO = {
  visual: { label: 'Visual Learner', icon: '👁️', color: '#20acf6', desc: 'You learn best through images, videos, and visual demonstrations. Look for courses with video content and image hotspots.' },
  auditory: { label: 'Auditory Learner', icon: '👂', color: '#02d1d4', desc: 'You learn best by listening. Audio elements and discussion-based content will suit your style.' },
  reading: { label: 'Reading/Writing Learner', icon: '📖', color: '#2a3e5d', desc: 'You learn best through reading and writing. Text content, reflection journals, and written resources are ideal for you.' },
  kinesthetic: { label: 'Kinesthetic Learner', icon: '✋', color: '#10B981', desc: 'You learn best by doing. Interactive quizzes, matching exercises, and hands-on activities will engage you most.' },
};

const LearningStyleQuiz = ({ open, onClose }) => {
  const { api, user, refreshUser } = useAuth();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleAnswer = (style) => {
    const newAnswers = [...answers, style];
    setAnswers(newAnswers);

    if (newAnswers.length === QUESTIONS.length) {
      // Calculate dominant style
      const counts = {};
      newAnswers.forEach(s => { counts[s] = (counts[s] || 0) + 1; });
      const dominant = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
      setResult(dominant);
      saveResult(dominant);
    } else {
      setStep(newAnswers.length);
    }
  };

  const saveResult = async (style) => {
    setSaving(true);
    try {
      await api.put('/auth/me', { learning_style: style, learning_quiz_completed: true });
      if (refreshUser) refreshUser();
    } catch {
      toast.error('Failed to save preference');
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = async () => {
    try {
      await api.put('/auth/me', { learning_quiz_completed: true });
      if (refreshUser) refreshUser();
    } catch { /* ignore */ }
    onClose();
  };

  const handleDone = () => {
    onClose();
    toast.success(`Learning style saved: ${STYLE_INFO[result]?.label}`);
  };

  const currentQ = QUESTIONS[step];
  const progress = ((step) / QUESTIONS.length) * 100;
  const styleData = result ? STYLE_INFO[result] : null;

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-lg" onPointerDownOutside={e => e.preventDefault()} data-testid="learning-quiz-modal">
        <DialogHeader>
          <DialogTitle className="text-[#2a3e5d] flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#20acf6]" />
            {result ? 'Your Learning Style' : 'Discover Your Learning Style'}
          </DialogTitle>
          <DialogDescription>
            {result ? 'Here are your results!' : 'Answer 5 quick questions to personalise your learning experience.'}
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-5">
            {/* Progress */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-2 bg-[#f8fafc] rounded-full overflow-hidden">
                <div className="h-full bg-[#20acf6] rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
              </div>
              <span className="text-xs text-slate-500 shrink-0">{step + 1} / {QUESTIONS.length}</span>
            </div>

            {/* Question */}
            <p className="text-base font-medium text-[#1e293b]" data-testid="quiz-question">{currentQ.q}</p>

            {/* Options */}
            <div className="grid gap-2">
              {currentQ.options.map((opt, i) => (
                <button
                  key={i}
                  onClick={() => handleAnswer(opt.style)}
                  className="text-left p-3 rounded-xl border border-slate-200 hover:border-[#20acf6] hover:bg-[#20acf6]/5 transition-all text-sm text-[#1e293b]"
                  data-testid={`quiz-option-${i}`}
                >
                  {opt.text}
                </button>
              ))}
            </div>

            {/* Skip */}
            <Button variant="ghost" size="sm" className="text-slate-400 text-xs w-full" onClick={handleSkip} data-testid="quiz-skip">
              <SkipForward className="h-3.5 w-3.5 mr-1" /> Skip for now
            </Button>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Result */}
            <div className="text-center py-4">
              <div className="text-5xl mb-3">{styleData.icon}</div>
              <Badge className="text-sm px-4 py-1.5 mb-2" style={{ backgroundColor: `${styleData.color}20`, color: styleData.color }}>
                {styleData.label}
              </Badge>
              <p className="text-sm text-slate-600 mt-3 leading-relaxed max-w-sm mx-auto">{styleData.desc}</p>
            </div>

            <Button
              className="w-full bg-[#20acf6] hover:bg-[#3bb8f7]"
              onClick={handleDone}
              disabled={saving}
              data-testid="quiz-done-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ArrowRight className="h-4 w-4 mr-2" />}
              Continue to Dashboard
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LearningStyleQuiz;
